import {
  useInviteMobileUser,
  useMobileUsers,
  useReactivateMobileUser,
  useRemoveMobileUser,
  useSuspendMobileUser,
} from '@my/directory-api/hooks';
import type { MobileUserRoleValue, MobileUserSummary } from '@my/directory-api';
import {
  ConfirmDialog,
  ErrorBanner,
  Loading,
  SelectInput,
  TextInput,
  showToast,
  type SelectInputOption,
} from '@my/ui';
import {
  BadgeCheck,
  Ban,
  Plus,
  RotateCcw,
  Smartphone,
  Trash2,
  X,
} from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

export interface MobileUsersTabProps {
  /** Owning organisation id, resolved by the parent page from JWT claims. */
  organisationId: string;
}

const MOBILE_ROLES: MobileUserRoleValue[] = [
  'Driver',
  'Mechanic',
  'Operative',
  'Supervisor',
  'Other',
];

interface InviteForm {
  displayName: string;
  mobileNumberE164: string;
  role: '' | MobileUserRoleValue;
}

const EMPTY_FORM: InviteForm = { displayName: '', mobileNumberE164: '', role: '' };

/**
 * Mobile Users tab — admin management of the phone-authenticated identities
 * (drivers / operatives) that use the Safety mobile app. Distinct from the
 * Users tab, which manages web (Auth0) logins.
 *
 * A mobile user is allow-listed here by phone number (Invite); they then
 * register a device via the OTP flow on the app, which flips them Active.
 * Admins can Suspend / Reactivate (blocks auth) or Remove (drops them from
 * this account). All wired to `@my/directory-api` mobile-user endpoints.
 */
export function MobileUsersTab({ organisationId }: MobileUsersTabProps) {
  const { t } = useTranslation('organisation');

  const usersQuery = useMobileUsers(organisationId, 'Safety');
  const inviteMutation = useInviteMobileUser();
  const suspendMutation = useSuspendMobileUser();
  const reactivateMutation = useReactivateMobileUser();
  const removeMutation = useRemoveMobileUser();

  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState<InviteForm>(EMPTY_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [pendingRemove, setPendingRemove] = useState<MobileUserSummary | null>(null);

  const rows = usersQuery.data ?? [];
  const isBusy =
    inviteMutation.isPending ||
    suspendMutation.isPending ||
    reactivateMutation.isPending ||
    removeMutation.isPending;

  const roleOptions: SelectInputOption[] = [
    { value: '', label: t('mobileUsers.fields.roleNone') },
    ...MOBILE_ROLES.map((r) => ({ value: r, label: t(`mobileUsers.roles.${r}`, { defaultValue: r }) })),
  ];

  const nameInvalid = !form.displayName.trim();
  const phoneInvalid = !form.mobileNumberE164.trim();

  const openInvite = () => {
    setForm(EMPTY_FORM);
    setSubmitted(false);
    setShowInvite(true);
  };

  const handleInvite = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitted(true);
    if (nameInvalid || phoneInvalid) return;
    try {
      await inviteMutation.mutateAsync({
        organisationId,
        displayName: form.displayName.trim(),
        mobileNumberE164: form.mobileNumberE164.trim(),
        role: form.role || null,
        appKey: 'Safety',
        appRole: 'User',
      });
      showToast({ type: 'success', message: t('mobileUsers.invited') });
      setForm(EMPTY_FORM);
      setSubmitted(false);
      setShowInvite(false);
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('mobileUsers.inviteFailed'),
      });
    }
  };

  const handleSuspend = async (row: MobileUserSummary) => {
    try {
      await suspendMutation.mutateAsync(row.id);
      showToast({ type: 'success', message: t('mobileUsers.suspended', { name: row.displayName }) });
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('mobileUsers.actionFailed'),
      });
    }
  };

  const handleReactivate = async (row: MobileUserSummary) => {
    try {
      await reactivateMutation.mutateAsync(row.id);
      showToast({ type: 'success', message: t('mobileUsers.reactivated', { name: row.displayName }) });
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('mobileUsers.actionFailed'),
      });
    }
  };

  const confirmRemove = async () => {
    const row = pendingRemove;
    if (!row) return;
    try {
      await removeMutation.mutateAsync({ id: row.id, organisationId, app: 'Safety' });
      showToast({ type: 'success', message: t('mobileUsers.removed', { name: row.displayName }) });
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('mobileUsers.actionFailed'),
      });
    } finally {
      setPendingRemove(null);
    }
  };

  return (
    <section className="rounded-md border border-slate-200 bg-white">
      <header className="flex items-center justify-between gap-3 border-b border-slate-200 p-4">
        <p className="text-xs text-slate-500">{t('mobileUsers.description')}</p>
        {!showInvite && (
          <button
            type="button"
            aria-label={t('mobileUsers.addAria')}
            onClick={openInvite}
            disabled={isBusy || usersQuery.isPending}
            className="flex shrink-0 items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-purple-600 hover:bg-purple-50 disabled:opacity-50"
          >
            <Plus className="h-3.5 w-3.5" />
            {t('mobileUsers.add')}
          </button>
        )}
      </header>

      {/* Invite panel */}
      {showInvite && (
        <form
          onSubmit={handleInvite}
          className="space-y-3 border-b border-slate-200 bg-purple-50/40 p-4"
        >
          <h3 className="text-xs font-medium uppercase tracking-wide text-slate-700">
            {t('mobileUsers.inviteTitle')}
          </h3>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <TextInput
              label={t('mobileUsers.fields.displayName')}
              name="mobile-displayName"
              autoComplete="off"
              value={form.displayName}
              onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
              error={submitted && nameInvalid}
              errorMessage={t('mobileUsers.fields.displayNameRequired')}
            />
            <TextInput
              label={t('mobileUsers.fields.mobileNumber')}
              name="mobile-number"
              autoComplete="off"
              placeholder={t('mobileUsers.fields.mobileNumberPlaceholder')}
              value={form.mobileNumberE164}
              onChange={(e) => setForm((f) => ({ ...f, mobileNumberE164: e.target.value }))}
              error={submitted && phoneInvalid}
              errorMessage={t('mobileUsers.fields.mobileNumberRequired')}
            />
            <SelectInput
              label={t('mobileUsers.fields.role')}
              name="mobile-role"
              value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as InviteForm['role'] }))}
              options={roleOptions}
            />
          </div>
          <p className="text-[11px] text-slate-500">{t('mobileUsers.inviteHint')}</p>
          <div className="flex items-center justify-end gap-1">
            <button
              type="button"
              aria-label={t('mobileUsers.ariaCancel')}
              onClick={() => setShowInvite(false)}
              disabled={isBusy}
              className="flex h-7 items-center gap-1 rounded-sm px-2 text-xs text-slate-500 hover:bg-slate-200 disabled:opacity-50"
            >
              <X className="h-3.5 w-3.5" />
              {t('mobileUsers.cancel')}
            </button>
            <button
              type="submit"
              disabled={isBusy}
              className="flex h-7 items-center gap-1 rounded-sm bg-purple-600 px-2.5 text-xs font-medium text-white hover:bg-purple-700 disabled:opacity-50"
            >
              <Plus className="h-3.5 w-3.5" />
              {t('mobileUsers.sendInvite')}
            </button>
          </div>
        </form>
      )}

      {usersQuery.isPending && (
        <div className="p-4">
          <Loading label={t('mobileUsers.loading')} />
        </div>
      )}
      {usersQuery.isError && (
        <div className="p-4">
          <ErrorBanner title={t('mobileUsers.loadFailed')} message={usersQuery.error.message} />
        </div>
      )}

      {usersQuery.isSuccess && rows.length === 0 && !showInvite && (
        <div className="flex items-center justify-center p-10 text-center">
          <div>
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
              <Smartphone className="h-6 w-6" />
            </div>
            <p className="text-sm font-medium text-slate-700">{t('mobileUsers.emptyTitle')}</p>
            <p className="mt-1 text-xs text-slate-500">{t('mobileUsers.emptyHint')}</p>
          </div>
        </div>
      )}

      {usersQuery.isSuccess && rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2 font-medium">{t('mobileUsers.table.name')}</th>
                <th className="px-4 py-2 font-medium">{t('mobileUsers.table.mobile')}</th>
                <th className="px-4 py-2 font-medium">{t('mobileUsers.table.role')}</th>
                <th className="px-4 py-2 font-medium">{t('mobileUsers.table.status')}</th>
                <th className="px-4 py-2 font-medium">{t('mobileUsers.table.devices')}</th>
                <th className="px-4 py-2 text-right font-medium">{t('mobileUsers.table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900">{row.displayName}</span>
                      {row.identityVerified && (
                        <BadgeCheck
                          className="h-3.5 w-3.5 text-emerald-600"
                          aria-label={t('mobileUsers.verified')}
                        />
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-600">
                    {row.mobileNumberE164}
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">
                    {row.role ? t(`mobileUsers.roles.${row.role}`, { defaultValue: row.role }) : '—'}
                  </td>
                  <td className="px-4 py-2.5">
                    <StatusBadge status={row.status} label={t(`mobileUsers.status.${row.status}`)} />
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">{row.deviceCount}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-end gap-1">
                      {row.status === 'Suspended' ? (
                        <button
                          type="button"
                          aria-label={t('mobileUsers.ariaReactivate', { name: row.displayName })}
                          title={t('mobileUsers.reactivate')}
                          onClick={() => handleReactivate(row)}
                          disabled={isBusy}
                          className="flex h-7 w-7 items-center justify-center rounded-sm text-emerald-600 hover:bg-emerald-50 disabled:opacity-50"
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          aria-label={t('mobileUsers.ariaSuspend', { name: row.displayName })}
                          title={t('mobileUsers.suspend')}
                          onClick={() => handleSuspend(row)}
                          disabled={isBusy}
                          className="flex h-7 w-7 items-center justify-center rounded-sm text-amber-600 hover:bg-amber-50 disabled:opacity-50"
                        >
                          <Ban className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button
                        type="button"
                        aria-label={t('mobileUsers.ariaRemove', { name: row.displayName })}
                        title={t('mobileUsers.remove')}
                        onClick={() => setPendingRemove(row)}
                        disabled={isBusy}
                        className="flex h-7 w-7 items-center justify-center rounded-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={pendingRemove !== null}
        tone="danger"
        title={t('mobileUsers.removeDialog.title')}
        message={
          pendingRemove
            ? t('mobileUsers.removeDialog.message', { name: pendingRemove.displayName })
            : null
        }
        confirmLabel={t('mobileUsers.removeDialog.confirm')}
        onConfirm={confirmRemove}
        onCancel={() => setPendingRemove(null)}
        busy={removeMutation.isPending}
      />
    </section>
  );
}

const STATUS_BADGE: Record<MobileUserSummary['status'], string> = {
  Invited: 'bg-amber-50 text-amber-700 ring-amber-200',
  Active: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  Suspended: 'bg-red-50 text-red-700 ring-red-200',
  Deleted: 'bg-slate-100 text-slate-500 ring-slate-200',
};

function StatusBadge({ status, label }: { status: MobileUserSummary['status']; label: string }) {
  return (
    <span
      className={
        'inline-flex items-center rounded-sm px-1.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ' +
        STATUS_BADGE[status]
      }
    >
      {label}
    </span>
  );
}
